// =============================================================================
// useLeaderboardReceipts — P0.8-Receipts
// =============================================================================
// Sibling to useLeaderboard. Fetches per-rep, per-metric receipt arrays from
// the get_leaderboard_receipts RPC. Used by ReceiptsPopover + ReceiptsModal
// on /admin/leaderboard.
//
// Why a SIBLING hook (not folded into useLeaderboard):
//   - Receipts are leaf data with their own cache surface.
//   - Folding doubles the cache invalidation risk and entangles realtime ticks.
//   - Independent abort + retry semantics on receipts vs aggregate KPIs.
//
// No realtime subscription: receipts re-fetch only on filter change.
// Stale-while-revalidate via 2-min localStorage cache (mirrors useLeaderboard).
// Company-wide view fans out one RPC call per org and merges results client-side.
// =============================================================================

import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "../../lib/supabase";
import type { LeaderboardReceiptsMap, ReceiptsByMetric, Receipt } from "../../types";
import type { SectorFilter } from "../../components/leaderboard/constants";
import { logger } from "../../lib/logger";
import { CACHE_VERSION } from "../../lib/cacheVersion";

// =============================================================================
// Cache configuration — mirrors useLeaderboard, prefix _lbr_ to avoid collision
// =============================================================================
const LBR_CACHE_PREFIX = `altara_${CACHE_VERSION}_lbr_`;
const LBR_CACHE_TTL = 2 * 60 * 1000; // 2 minutes

interface CacheEntry {
  data: LeaderboardReceiptsMap;
  timestamp: number;
  orgKey: string;
}

function getLbrCache(key: string, orgKey: string): LeaderboardReceiptsMap | null {
  try {
    const raw = localStorage.getItem(LBR_CACHE_PREFIX + key);
    if (!raw) return null;
    const entry: CacheEntry = JSON.parse(raw);
    if (Date.now() - entry.timestamp > LBR_CACHE_TTL || entry.orgKey !== orgKey) {
      localStorage.removeItem(LBR_CACHE_PREFIX + key);
      return null;
    }
    return entry.data;
  } catch {
    return null;
  }
}

function setLbrCache(key: string, data: LeaderboardReceiptsMap, orgKey: string): void {
  try {
    localStorage.setItem(
      LBR_CACHE_PREFIX + key,
      JSON.stringify({ data, timestamp: Date.now(), orgKey } satisfies CacheEntry)
    );
  } catch {
    /* localStorage full — ignore */
  }
}

function getLbrCacheKey(
  sortedOrgIds: string[],
  sector: string | undefined,
  podId: string | undefined,
  startDate: string,
  endDate: string
): string {
  return `${sortedOrgIds.join(",")}_${sector || "all"}_${podId || "all"}_${startDate}_${endDate}`;
}

// =============================================================================
// Public interface
// =============================================================================

export interface UseLeaderboardReceiptsOptions {
  orgIds: string[]; // company-wide fan-out; empty = no fetch
  startDate: string; // 'YYYY-MM-DD' org tz; empty = no fetch
  endDate: string;
  sector?: SectorFilter | "all";
  podId?: string;
  enabled?: boolean; // default true
}

export interface UseLeaderboardReceiptsResult {
  receipts: LeaderboardReceiptsMap;
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

// Shape returned by the RPC per row
interface RpcReceiptRow {
  rep_id: string;
  metric: string;
  receipts: Receipt[];
}

// =============================================================================
// Hook
// =============================================================================

export function useLeaderboardReceipts(
  opts: UseLeaderboardReceiptsOptions
): UseLeaderboardReceiptsResult {
  const { orgIds, startDate, endDate, sector, podId, enabled = true } = opts;

  const [receipts, setReceipts] = useState<LeaderboardReceiptsMap>({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const abortControllerRef = useRef<AbortController | null>(null);

  // Derive stable sorted org key once — used in cache key + orgKey field
  const sortedOrgIds = [...orgIds].sort();
  const orgKey = sortedOrgIds.join(",");

  // Sector mapped to RPC param: 'all' | undefined → null, else literal
  const sectorParam: string | null = sector === "all" || sector === undefined ? null : sector;

  // Short-circuit guard — M2
  const shouldFetch = enabled !== false && orgIds.length > 0 && startDate !== "" && endDate !== "";

  const doFetch = useCallback(
    async (signal: AbortSignal, forceRefresh = false): Promise<void> => {
      if (!shouldFetch || !supabase) return;

      const cacheKey = getLbrCacheKey(sortedOrgIds, sector, podId, startDate, endDate);

      // Stale-while-revalidate: on cache hit, populate immediately then re-fetch in background
      if (!forceRefresh) {
        const cached = getLbrCache(cacheKey, orgKey);
        if (cached) {
          setReceipts((prev) => {
            const next = cached;
            const prevKeys = Object.keys(prev);
            const nextKeys = Object.keys(next);
            if (
              prevKeys.length === nextKeys.length &&
              nextKeys.every((k) => JSON.stringify(prev[k]) === JSON.stringify(next[k]))
            ) {
              return prev;
            }
            return next;
          });
          setIsLoading(false);
          // Background re-fetch continues below (stale-while-revalidate)
        } else {
          setIsLoading(true);
        }
      } else {
        setIsLoading(true);
      }

      setError(null);

      try {
        logger.debug("[useLeaderboardReceipts] fetching", {
          orgIds: sortedOrgIds,
          startDate,
          endDate,
          sector: sectorParam,
          podId,
        });

        // H1: fan-out one RPC per org, merge results
        const podIdOrNull = podId ?? null;
        const results = await Promise.all(
          sortedOrgIds.map((orgId) =>
            supabase!
              .rpc("get_leaderboard_receipts", {
                p_organization_id: orgId,
                p_start: startDate,
                p_end: endDate,
                p_sector: sectorParam,
                p_pod_id: podIdOrNull,
              })
              .abortSignal(signal)
          )
        );

        if (signal.aborted) return;

        // Check for errors across all org fetches
        const firstError = results.find((r) => r.error);
        if (firstError?.error) {
          throw firstError.error;
        }

        // Merge all rows into one LeaderboardReceiptsMap keyed by rep_id
        const merged: LeaderboardReceiptsMap = {};

        for (const result of results) {
          const rows = (result.data ?? []) as RpcReceiptRow[];
          for (const row of rows) {
            const { rep_id, metric, receipts: rowReceipts } = row;
            if (!merged[rep_id]) {
              merged[rep_id] = {};
            }
            const metricKey = metric as keyof ReceiptsByMetric;
            const existing = merged[rep_id][metricKey];
            if (existing) {
              // Guard: rep appeared in multiple orgs — concat (shouldn't happen in practice)
              (merged[rep_id][metricKey] as Receipt[]) = [...existing, ...rowReceipts];
            } else {
              merged[rep_id][metricKey] = rowReceipts;
            }
          }
        }

        if (signal.aborted) return;

        // Stable referential identity — H2 + M5
        setReceipts((prev) => {
          const next = merged;
          const prevKeys = Object.keys(prev);
          const nextKeys = Object.keys(next);
          if (
            prevKeys.length === nextKeys.length &&
            nextKeys.every((k) => JSON.stringify(prev[k]) === JSON.stringify(next[k]))
          ) {
            return prev;
          }
          return next;
        });

        setLbrCache(cacheKey, merged, orgKey);
      } catch (err) {
        if (signal.aborted || (err as Error)?.name === "AbortError") return;
        const message = err instanceof Error ? err.message : "Failed to fetch leaderboard receipts";
        logger.error("[useLeaderboardReceipts] fetch error:", message);
        setError(message);
      } finally {
        if (!signal.aborted) {
          setIsLoading(false);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [orgKey, startDate, endDate, sector, podId, shouldFetch, sectorParam]
  );

  // H4: abort prior on filter change, create new controller
  const fetchWithCleanup = useCallback(
    async (forceRefresh = false): Promise<void> => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();
      await doFetch(abortControllerRef.current.signal, forceRefresh);
    },
    [doFetch]
  );

  // Initial fetch + re-fetch on filter changes
  useEffect(() => {
    if (!shouldFetch) {
      setReceipts({});
      setIsLoading(false);
      setError(null);
      return;
    }

    fetchWithCleanup(false);

    return () => {
      // H9: abort on unmount or dep change
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [shouldFetch, fetchWithCleanup]);

  // Public refresh: invalidate cache + force fetch
  const refresh = useCallback(async (): Promise<void> => {
    if (!shouldFetch) return;
    const cacheKey = getLbrCacheKey(sortedOrgIds, sector, podId, startDate, endDate);
    localStorage.removeItem(LBR_CACHE_PREFIX + cacheKey);
    await fetchWithCleanup(true);
  }, [shouldFetch, sortedOrgIds, sector, podId, startDate, endDate, fetchWithCleanup]);

  // Short-circuit return — M2 (when shouldFetch is false)
  if (!shouldFetch) {
    return {
      receipts: {},
      isLoading: false,
      error: null,
      refresh: async () => {},
    };
  }

  return { receipts, isLoading, error, refresh };
}
